// Mobile navigation: a menu button that opens the grouped sidebar in a drawer.

import {
  Avatar,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Icon,
  IconButton,
  Link as ChakraLink,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiMenu } from 'react-icons/fi';
import { MdLogout, MdSwapHoriz } from 'react-icons/md';
import {
  DASHBOARD_NAV_GROUPS,
  type DashboardNav,
  type DashboardNavLink,
} from './useDashboardNav';

function NavGroup({
  title,
  links,
  activeLinkKey,
  onNavigate,
}: {
  title: string;
  links: DashboardNavLink[];
  activeLinkKey: string | null;
  onNavigate: () => void;
}) {
  return (
    <VStack align="stretch" spacing={0.5} data-testid={`mobile-nav-group-${title.toLowerCase()}`}>
      <Text
        fontSize="xs"
        fontWeight="600"
        textTransform="uppercase"
        letterSpacing="wider"
        color="text-muted"
        px={3}
        pb={1}
      >
        {title}
      </Text>
      {links.map((link) => {
        const isActive = link.key === activeLinkKey;
        return (
          <ChakraLink
            key={link.key}
            as={RouterLink}
            to={link.path}
            onClick={onNavigate}
            data-testid={`mobile-nav-item-${link.key}`}
            aria-current={isActive ? 'page' : undefined}
            display="flex"
            alignItems="center"
            gap={3}
            px={3}
            py={2}
            borderRadius="md"
            fontSize="sm"
            fontWeight={isActive ? '600' : '500'}
            color={isActive ? 'brand.600' : 'text-secondary'}
            bg={isActive ? 'interactive-brand-hover' : 'transparent'}
            _dark={{ color: isActive ? 'brand.300' : 'text-secondary' }}
            _hover={{ textDecoration: 'none', bg: isActive ? 'interactive-brand-hover' : 'interactive-hover' }}
          >
            <Icon as={link.icon} boxSize={4} />
            {link.label}
          </ChakraLink>
        );
      })}
    </VStack>
  );
}

export function DashboardMobileNav({ nav }: { nav: DashboardNav }) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const groups = DASHBOARD_NAV_GROUPS.map((group) => ({
    title: group as string,
    links: nav.sections.filter((section) => section.group === group).flatMap((section) => section.links),
  }));
  if (nav.adminLinks.length > 0) groups.push({ title: 'Admin', links: nav.adminLinks });

  return (
    <>
      <IconButton
        aria-label="Open menu"
        data-testid="mobile-nav-open"
        icon={<FiMenu size={20} />}
        variant="ghost"
        color="text-primary"
        onClick={onOpen}
      />
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent bg="bg-canvas" data-testid="mobile-nav-drawer">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor="border-default" pb={3}>
            <Text fontSize="xs" color="text-muted" fontWeight="500">
              Organization
            </Text>
            <Text fontSize="md" color="text-primary">
              {nav.orgName}
            </Text>
            <Button
              size="xs"
              variant="link"
              leftIcon={<MdSwapHoriz />}
              mt={1}
              onClick={() => {
                onClose();
                nav.switchOrg();
              }}
            >
              Switch organization
            </Button>
          </DrawerHeader>
          <DrawerBody py={4}>
            <VStack align="stretch" spacing={5}>
              {groups
                .filter((group) => group.links.length > 0)
                .map((group) => (
                  <NavGroup
                    key={group.title}
                    title={group.title}
                    links={group.links}
                    activeLinkKey={nav.activeLinkKey}
                    onNavigate={onClose}
                  />
                ))}
            </VStack>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px" borderColor="border-default" justifyContent="space-between">
            <HStack spacing={2} minW={0}>
              <Avatar size="sm" name={nav.userName} bg="brand.500" color="white" />
              <Box minW={0}>
                <Text fontSize="sm" fontWeight="500" color="text-primary" noOfLines={1}>
                  {nav.userName}
                </Text>
              </Box>
            </HStack>
            <Button size="sm" variant="ghost" color="red.600" leftIcon={<MdLogout />} onClick={nav.logout}>
              Log out
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
